import React from "react";
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sparkles, Code, Database, Shield, CreditCard, Layers, RefreshCw } from 'lucide-react';
import { ProjectMemory } from '@/hooks/useProjectMemory';

interface ExplainAppDialogProps {
  memory: ProjectMemory | null;
  projectName?: string;
  files?: Array<{ name: string; isFolder?: boolean }>;
  isLoading?: boolean;
}

interface AppExplanation {
  purpose: string;
  coreFeatures: string[];
  techStack: { category: string; items: string[] }[];
  dataModels: string[];
  authPayments: { auth: string; payments: string };
  structure: string[];
}

export function ExplainAppDialog({ memory, projectName, files = [], isLoading }: ExplainAppDialogProps) {
  const [explanation, setExplanation] = useState<AppExplanation | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open && memory) {
      generateExplanation();
    }
  }, [open, memory]);

  const generateExplanation = () => {
    if (!memory) return;

    // Extract tech stack from memory
    const techStack: { category: string; items: string[] }[] = [];
    if (memory.tech_stack) {
      if (memory.tech_stack.framework) {
        techStack.push({ category: 'Framework', items: [memory.tech_stack.framework] });
      }
      if (memory.tech_stack.ui) {
        techStack.push({ category: 'UI Library', items: [memory.tech_stack.ui] });
      }
      if (memory.tech_stack.backend) {
        techStack.push({ category: 'Backend', items: [memory.tech_stack.backend] });
      }
      if (memory.tech_stack.database) {
        techStack.push({ category: 'Database', items: [memory.tech_stack.database] });
      }
    }

    // Default tech stack if not specified
    if (techStack.length === 0) {
      techStack.push(
        { category: 'Framework', items: ['React + TypeScript'] },
        { category: 'Styling', items: ['Tailwind CSS'] },
        { category: 'Backend', items: ['Supabase'] }
      );
    }

    // Extract features
    const coreFeatures = memory.features.length > 0
      ? memory.features.map(f => f.name)
      : ['No features documented yet'];

    // Infer data models from architecture decisions and features
    const dataModels: string[] = [];
    memory.architecture_decisions.forEach(d => {
      if (d.decision.toLowerCase().includes('table') || d.decision.toLowerCase().includes('model')) {
        dataModels.push(d.decision);
      }
    });
    if (dataModels.length === 0) {
      dataModels.push('Data models will be documented as features are added');
    }

    // Check for auth/payments
    const hasAuth = memory.tech_stack?.authentication || 
      memory.features.some(f => f.name.toLowerCase().includes('auth') || f.description.toLowerCase().includes('auth'));
    const hasPayments = memory.tech_stack?.payments ||
      memory.features.some(f => f.name.toLowerCase().includes('payment') || f.name.toLowerCase().includes('stripe'));

    // Infer structure from files
    const structure: string[] = [];
    const hasPages = files.some(f => f.name.includes('/pages/') || f.name.includes('/app/'));
    const hasComponents = files.some(f => f.name.includes('/components/'));
    const hasHooks = files.some(f => f.name.includes('/hooks/'));
    const hasApi = files.some(f => f.name.includes('/api/') || f.name.includes('/functions/'));
    
    if (hasPages) structure.push('Pages/Routes for navigation');
    if (hasComponents) structure.push('Reusable UI components');
    if (hasHooks) structure.push('Custom React hooks for logic');
    if (hasApi) structure.push('API routes/Edge functions');
    if (structure.length === 0) structure.push('Standard React application structure');

    setExplanation({
      purpose: memory.app_purpose || 'App purpose not yet defined. Start building to let the AI learn about your app!',
      coreFeatures,
      techStack,
      dataModels,
      authPayments: {
        auth: hasAuth ? 'Authentication enabled (email/password, OAuth)' : 'No authentication configured',
        payments: hasPayments ? 'Payment processing enabled (Stripe)' : 'No payment processing configured'
      },
      structure
    });
  };

  const Section = ({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) => (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <Icon className="h-4 w-4 text-primary" />
        {title}
      </div>
      <div className="pl-6 text-sm text-muted-foreground">
        {children}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Explain My App">
          <Sparkles className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {projectName || 'Your App'} — Explained
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="h-[60vh] pr-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : explanation ? (
            <div className="space-y-6">
              <Section icon={Sparkles} title="App Purpose">
                <p>{explanation.purpose}</p>
              </Section>

              <Section icon={Layers} title="Core Features">
                <ul className="list-disc list-inside space-y-1">
                  {explanation.coreFeatures.map((feature, i) => (
                    <li key={i}>{feature}</li>
                  ))}
                </ul>
              </Section>

              <Section icon={Code} title="Tech Stack">
                <div className="space-y-2">
                  {explanation.techStack.map((stack, i) => (
                    <div key={i}>
                      <span className="font-medium text-foreground">{stack.category}:</span>{' '}
                      {stack.items.join(', ')}
                    </div>
                  ))}
                </div>
              </Section>

              <Section icon={Database} title="Data Models">
                <ul className="list-disc list-inside space-y-1">
                  {explanation.dataModels.map((model, i) => (
                    <li key={i}>{model}</li>
                  ))}
                </ul>
              </Section>

              <Section icon={Shield} title="Authentication">
                <p>{explanation.authPayments.auth}</p>
              </Section>

              <Section icon={CreditCard} title="Payments">
                <p>{explanation.authPayments.payments}</p>
              </Section>

              <Section icon={Layers} title="App Structure">
                <ul className="list-disc list-inside space-y-1">
                  {explanation.structure.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </Section>

              <div className="pt-4 border-t text-xs text-muted-foreground">
                This explanation updates automatically as you build. The AI learns from your prompts and code changes.
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              No project memory available yet. Start building to generate insights!
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
